import type { RedoReturnsProcessingRpcClient } from "@redotech/redo-merchant-returns-processing-rpc/client";
import { createRedoReturnsProcessingRpcClient } from "@redotech/redo-merchant-returns-processing-rpc/client";
import { ExponentialBackoffOptions } from "@redotech/util/retry";
import { Context, createContext, memo, ReactNode, useMemo } from "react";

export const RedoReturnsProcessingRpcClientContext: Context<
  RedoReturnsProcessingRpcClient | undefined
> = createContext<RedoReturnsProcessingRpcClient | undefined>(undefined);
RedoReturnsProcessingRpcClientContext.displayName =
  "RedoReturnsProcessingRpcClientContext";

export const RedoReturnsProcessingRpcClientProvider = memo(
  function RedoReturnsProcessingRpcClientProvider({
    children,
    baseUrl,
    authToken,
  }: {
    children: ReactNode | ReactNode[];
    baseUrl: string;
    authToken?: string;
  }) {
    const redoReturnsProcessingRpcClient = useMemo(() => {
      if (!authToken) {
        return undefined;
      }
      const retryOptions: ExponentialBackoffOptions = {
        maxAttempts: 2,
        baseDelay: Temporal.Duration.from({ milliseconds: 1000 }),
        maxDelay: Temporal.Duration.from({ seconds: 5 }),
        jitterFactor: 0.1,
        shouldRetry: (error) => {
          const statusCode = error?.status || error?.statusCode;
          return statusCode === 504 || statusCode === 502;
        },
      };
      return createRedoReturnsProcessingRpcClient({
        baseURL: new URL(`${baseUrl}/returns-processing-rpc`),
        headers: { Authorization: authToken },
        retryOptions,
      });
    }, [baseUrl, authToken]);
    return (
      <RedoReturnsProcessingRpcClientContext.Provider
        value={redoReturnsProcessingRpcClient}
      >
        {children}
      </RedoReturnsProcessingRpcClientContext.Provider>
    );
  },
);
